import { useEffect, useRef } from "react";
import { apiMutate } from "~/utils/apiClient";

const { VITE_ANALYSIS_AGENT_API, VITE_CUSTOMER } = import.meta.env;

interface McpAppPaneFrameProps {
  html: string;
  title: string;
  serverId: string;
  // Latest render state; pushed into the iframe on init and on every change.
  state: unknown;
  // Called with the fresh state after a panel-initiated tool call, so the pane
  // can keep the tab (title, dedupe key) in sync with edits made in the panel.
  onLocalUpdate: (state: unknown) => void;
}

/**
 * A pane-hosted MCP-app iframe that pushes state updates in place and routes
 * panel-initiated edits back to the server. Speaks the `ui/initialize` →
 * `ui/tool_result` postMessage contract as `McpAppFrame`, re-posting whenever
 * `state` changes so a single iframe reflects the agent's running edits rather
 * than remounting per tool call. Additionally, a `tools/call` message from the
 * iframe (the app-to-server direction) is proxied to the analysis-agent, and
 * the returned state is pushed straight back into the iframe.
 */
export function McpAppPaneFrame({
  html,
  title,
  serverId,
  state,
  onLocalUpdate,
}: McpAppPaneFrameProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const readyRef = useRef(false);
  // Read inside the message handler without re-subscribing on every change.
  const stateRef = useRef(state);
  stateRef.current = state;
  const serverIdRef = useRef(serverId);
  serverIdRef.current = serverId;
  const onLocalUpdateRef = useRef(onLocalUpdate);
  onLocalUpdateRef.current = onLocalUpdate;

  // A new html bundle reloads the iframe, so it must re-initialize.
  useEffect(() => {
    readyRef.current = false;
  }, [html]);

  useEffect(() => {
    function postResult(content: unknown, id?: unknown) {
      iframeRef.current?.contentWindow?.postMessage(
        { type: "ui/tool_result", id, result: { content } },
        "*"
      );
    }

    function handleMessage(event: MessageEvent) {
      // srcDoc iframes have an opaque ("null") origin, so event.origin can't be
      // allowlisted — checking event.source against this iframe's contentWindow
      // is the equivalent check. Same reason postMessage below targets "*"
      // rather than a specific origin: there is no URL origin to target.
      const iframe = iframeRef.current;
      if (!iframe || event.source !== iframe.contentWindow) return;

      const data = event.data as
        | {
            type?: string;
            id?: unknown;
            name?: string;
            arguments?: Record<string, unknown>;
          }
        | undefined;

      if (data?.type === "ui/initialize") {
        readyRef.current = true;
        postResult(stateRef.current);
        iframe.contentWindow?.postMessage({ type: "ui/theme", customer: VITE_CUSTOMER }, "*");
        return;
      }

      if (data?.type === "tools/call" && typeof data.name === "string") {
        const callId = data.id;
        const name = data.name;
        apiMutate<unknown>(`${VITE_ANALYSIS_AGENT_API}/mcp-resources/call`, "POST", {
          server_id: serverIdRef.current,
          tool: name,
          arguments: data.arguments ?? {},
        })
          .then((result) => {
            onLocalUpdateRef.current(result);
            postResult(result, callId);
          })
          .catch((error: unknown) => {
            iframe.contentWindow?.postMessage(
              {
                type: "ui/tool_result",
                id: callId,
                error: { message: String(error) },
              },
              "*"
            );
          });
      }
    }
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (readyRef.current && iframe?.contentWindow) {
      iframe.contentWindow.postMessage({ type: "ui/tool_result", result: { content: state } }, "*");
    }
  }, [state]);

  return (
    <iframe
      ref={iframeRef}
      srcDoc={html}
      sandbox="allow-scripts allow-top-navigation-by-user-activation"
      title={title}
      style={{
        width: "100%",
        height: "100%",
        border: "none",
        display: "block",
      }}
    />
  );
}
